import { useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCount, formatUsd, formatUsdCompact, prettyQuarter } from '@/lib/format';
import { colorForRegion } from '@/lib/titleCharts';
import { useResolvedColors } from '@/lib/useResolvedColor';
import { SegmentedControl } from './SegmentedControl';
import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import { QuarterRangeSlider } from './QuarterRangeSlider';
import { ChartSummary, Emph } from './ChartSummary';
import { ChartToolbar, ToolbarGroup, QuarterTick, formatAxisCount, useQuarterWindow, profileChartStyles as s } from './ProfileSection';

interface HistoryPoint {
  quarter: string;
  revenue: number;
  subscribers: number;
}

export interface PerformanceOverTimeChartProps {
  platform: string;
  /** Scoped history (all regions, or the picked region). */
  total: HistoryPoint[];
  /** Per-region history, for the stacked breakdown. */
  byRegion: Partial<Record<string, HistoryPoint[]>>;
  /** Regions to stack — all four, or just the picked one. */
  regions: string[];
  /** "all regions (UCAN, EMEA, APAC, LATAM)" / "UCAN". */
  scopeLabel: string;
  /** Revenue rank among platforms in the latest quarter, for the sentence. */
  revenueRank?: { rank: number; of: number };
}

const LABEL_ALL_MAX = 12;
const TOTAL = 'Total';

type Metric = 'both' | 'revenue' | 'subscribers';
type Period = 'quarterly' | 'cumulative';

const BREAKDOWN_OPTS = [
  { value: 'regions', label: 'Regions' },
  { value: 'none', label: 'None' },
];
const METRIC_OPTS = [
  { value: 'both', label: 'Revenue + Subscribers' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'subscribers', label: 'Subscribers' },
];
const PERIOD_OPTS = [
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'cumulative', label: 'Cumulative' },
];

/**
 * Performance Over Time — the old platform page's stacked revenue by region
 * with subscribers as a line on a right axis, in the title profile's chart
 * anatomy (sentence, ruled toolbar, range slider, two-line ticks).
 */
export function PerformanceOverTimeChart({
  platform,
  total,
  byRegion,
  regions,
  scopeLabel,
  revenueRank,
}: PerformanceOverTimeChartProps) {
  const [breakdown, setBreakdown] = useState<'regions' | 'none'>('regions');
  const [metric, setMetric] = useState<Metric>('both');
  const [period, setPeriod] = useState<Period>('quarterly');

  const showRevenue = metric !== 'subscribers';
  const showSubs = metric !== 'revenue';
  const cumulative = period === 'cumulative' && showRevenue;
  const series = breakdown === 'regions' ? regions : [TOTAL];

  // Running totals are taken over the full history, then windowed.
  const allPoints = useMemo(() => {
    const regionRev = (r: string, q: string) => byRegion[r]?.find((h) => h.quarter === q)?.revenue ?? 0;
    const running: Record<string, number> = {};
    return total.map((h) => {
      const p: Record<string, number | string> = {
        quarter: h.quarter,
        label: prettyQuarter(h.quarter),
        subscribers: h.subscribers,
      };
      for (const name of series) {
        const v = name === TOTAL ? h.revenue : regionRev(name, h.quarter);
        running[name] = (running[name] ?? 0) + v;
        p[name] = cumulative ? running[name] : v;
      }
      return p;
    });
  }, [total, byRegion, series.join('|'), cumulative]);

  const [[lo, hi], setRange] = useQuarterWindow(allPoints.length);
  const points = allPoints.slice(lo, hi + 1);

  const tokens = series.map((n) => (n === TOTAL ? 'var(--accent-secondary)' : colorForRegion(n)));
  const colors = useResolvedColors([...tokens, 'var(--text-primary)']);
  const lineColor = colors[tokens.length];

  const sumAt = (p: Record<string, number | string> | undefined) =>
    p ? series.reduce((acc, n) => acc + (Number(p[n]) || 0), 0) : 0;
  const last = points[points.length - 1];
  const lastQ = last ? String(last.label) : '—';
  const isLatest = hi === allPoints.length - 1;
  const showAllLabels = points.length <= LABEL_ALL_MAX;

  const yLeft = cumulative ? 'Cumulative Revenue (USD)' : 'Revenue (USD)';

  return (
    <>
      <ChartSummary className={s.summary}>
        <Emph>{platform}</Emph>{' '}
        {cumulative ? (
          <>
            generated <Emph>{formatUsdCompact(sumAt(last))}</Emph> in cumulative revenue from{' '}
            <Emph>{allPoints[0] ? String(allPoints[0].label) : '—'}</Emph> to <Emph>{lastQ}</Emph>
          </>
        ) : (
          <>
            generated <Emph>{formatUsdCompact(sumAt(last))}</Emph> in revenue in <Emph>{lastQ}</Emph>
          </>
        )}{' '}
        across {scopeLabel}, with <Emph>{last ? formatCount(Number(last.subscribers)) : '—'}</Emph> subscribers
        {isLatest && !cumulative && revenueRank ? (
          <>
            , ranking <Emph>#{revenueRank.rank}</Emph> of {revenueRank.of} platforms by revenue
          </>
        ) : null}
        .
      </ChartSummary>

      <ChartToolbar>
        {showRevenue && regions.length > 1 && (
          <ToolbarGroup label="Breakdown">
            <SegmentedControl
              variant="field"
              options={BREAKDOWN_OPTS}
              value={breakdown}
              onChange={(v) => setBreakdown(v as 'regions' | 'none')}
            />
          </ToolbarGroup>
        )}
        <ToolbarGroup label="Metric">
          <SegmentedControl variant="field" options={METRIC_OPTS} value={metric} onChange={(v) => setMetric(v as Metric)} />
        </ToolbarGroup>
        {showRevenue && (
          <ToolbarGroup label="Period">
            <SegmentedControl variant="field" options={PERIOD_OPTS} value={period} onChange={(v) => setPeriod(v as Period)} />
          </ToolbarGroup>
        )}
      </ChartToolbar>

      <div className={s.body}>
        {allPoints.length > 1 && (
          <QuarterRangeSlider
            count={allPoints.length}
            value={[lo, hi]}
            onChange={setRange}
            labels={allPoints.map((p) => String(p.label))}
          />
        )}

        {points.length ? (
          <>
            <div className={s.plot}>
              <span className={`${s.yAxisTitle} ${s.yAxisTitleLeft}`}>{showRevenue ? yLeft : 'Number of Subscribers'}</span>
              <ResponsiveContainer width="100%" height={380}>
                <ComposedChart data={points} margin={{ top: 24, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    axisLine={{ stroke: 'var(--border-default)' }}
                    tickLine={false}
                    interval={0}
                    height={46}
                    tick={QuarterTick}
                  />
                  {showRevenue && (
                    <YAxis
                      yAxisId="rev"
                      className={s.axis}
                      axisLine={false}
                      tickLine={false}
                      width={64}
                      tickFormatter={(v) => formatUsdCompact(Number(v))}
                    />
                  )}
                  {showSubs && (
                    <YAxis
                      yAxisId="subs"
                      orientation={showRevenue ? 'right' : 'left'}
                      className={s.axis}
                      axisLine={false}
                      tickLine={false}
                      width={64}
                      tickFormatter={(v) => formatAxisCount(Number(v))}
                    />
                  )}
                  <Tooltip
                    cursor={{ fill: 'var(--bg-hover)', opacity: 0.4 }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as Record<string, number | string>;
                      return (
                        <ChartTooltip
                          title={label}
                          rows={
                            showRevenue
                              ? series
                                  .slice()
                                  .reverse()
                                  .map((n) => ({
                                    label: n === TOTAL ? 'Revenue' : n,
                                    value: formatUsd(Number(p[n]) || 0),
                                    color: colors[series.indexOf(n)],
                                  }))
                              : []
                          }
                          totals={[
                            ...(showRevenue && series.length > 1
                              ? [{ label: cumulative ? 'Cumulative revenue' : 'Revenue', value: formatUsd(sumAt(p)) }]
                              : []),
                            ...(showSubs ? [{ label: 'Subscribers', value: formatCount(Number(p.subscribers)) }] : []),
                          ]}
                        />
                      );
                    }}
                  />
                  {showRevenue &&
                    series.map((n, i) => (
                      <Bar
                        key={n}
                        yAxisId="rev"
                        dataKey={n}
                        stackId="rev"
                        fill={colors[i]}
                        radius={i === series.length - 1 ? [2, 2, 0, 0] : undefined}
                      >
                        {i === series.length - 1 && (
                          <LabelList
                            dataKey="label"
                            content={(props) => {
                              const { x, y, width, value } = props as { x: number; y: number; width: number; value: string };
                              if (!showAllLabels && value !== lastQ) return null;
                              const v = sumAt(points.find((pt) => pt.label === value));
                              return v ? (
                                <text x={x + width / 2} y={y - 6} textAnchor="middle" className={s.valueLabel}>
                                  {formatUsdCompact(v)}
                                </text>
                              ) : null;
                            }}
                          />
                        )}
                      </Bar>
                    ))}
                  {showSubs && (
                    <Line
                      yAxisId="subs"
                      dataKey="subscribers"
                      stroke={lineColor}
                      strokeWidth={2}
                      dot={{ r: 3, fill: lineColor, strokeWidth: 0 }}
                      activeDot={{ r: 5 }}
                      isAnimationActive={false}
                    >
                      {!showRevenue && (
                        <LabelList
                          dataKey="subscribers"
                          content={(props) => {
                            const { x, y, value, index } = props as { x: number; y: number; value: number; index: number };
                            if (!showAllLabels && index !== points.length - 1) return null;
                            return (
                              <text x={x} y={y - 10} textAnchor="middle" className={s.valueLabel}>
                                {formatCount(value)}
                              </text>
                            );
                          }}
                        />
                      )}
                    </Line>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
              {showRevenue && showSubs ? (
                <span className={`${s.yAxisTitle} ${s.yAxisTitleRight}`}>Number of Subscribers</span>
              ) : (
                <span />
              )}
            </div>

            <ChartLegend
              items={[
                ...(showRevenue ? series.map((n, i) => ({ label: n === TOTAL ? 'Revenue' : n, color: colors[i] })) : []),
                ...(showSubs ? [{ label: 'Subscribers', color: lineColor, shape: 'line' as const }] : []),
              ]}
            />
          </>
        ) : (
          <div className={s.empty}>No performance data available</div>
        )}
      </div>
    </>
  );
}
